/**
 * Checks the browser and its version, returning details and optionally enforcing a minimum version requirement.
 * This function supports Chrome, Firefox, Safari, Edge, and Opera.
 *
 * Usage:
 *   const info = BrowserChecker.check({minVersions: {chrome: 104, firefox: 90}});
 *   if (!info.isSupported) alert('Please update your browser!');
 */

import { sys } from 'cc';

type Browser = 'chrome' | 'firefox' | 'safari' | 'edge' | 'opera' | 'samsung_internet' | 'unknown';

/* eslint-disable @typescript-eslint/naming-convention */
export interface BrowserInfo {
    name: Browser;
    version: number;
    isSupported: boolean;
    details: string;
}

export interface BroswerCheckOptions {
    minVersions?: Partial<Record<Browser, number>>;
    isDesktopSiteMode?: boolean;
}

export default class BrowserChecker {
    public static check(options?: BroswerCheckOptions): BrowserInfo {
        const nav = window.navigator;
        const ua = navigator.userAgent;
        let name: Browser = 'unknown';
        let version = 0;

        // Samsung Internet
        const samsungMatch = /\bSamsungBrowser\/(\d+)/.exec(ua);

        // Chrome (exclude Edge Chromium, Samsung Internet, Opera)
        const chromeMatch = /\b(?:Chrome|CriOS)\/(\d+)/.exec(ua) && !/\bEdg\//.test(ua) && !/\bOPR\//.test(ua) && !samsungMatch;

        // Edge Chromium
        const edgeMatch = /\bEdg\/(\d+)/.exec(ua);

        // Firefox
        const firefoxMatch = /\bFirefox\/(\d+)/.exec(ua);

        // Safari
        const safariMatch = /\bVersion\/([\d.]+)/.exec(ua);

        // Opera
        const operaMatch = /\bOPR\/(\d+)/.exec(ua);

        if (samsungMatch) {
            name = 'samsung_internet';
            version = parseInt(samsungMatch[1], 10);
        } else if (chromeMatch) {
            name = 'chrome';
            version = parseInt(/\b(?:Chrome|CriOS)\/(\d+)/.exec(ua)![1], 10);
        } else if (edgeMatch) {
            name = 'edge';
            version = parseInt(edgeMatch[1], 10);
        } else if (firefoxMatch) {
            name = 'firefox';
            version = parseInt(firefoxMatch[1], 10);
        } else if (safariMatch) {
            name = 'safari';
            version = parseInt(safariMatch[1], 10);
        } else if (operaMatch) {
            name = 'opera';
            version = parseInt(operaMatch[1], 10);
        }

        const minVer = options?.minVersions && name !== 'unknown' ? options.minVersions[name] : undefined;

        let isDesktopSiteMode: boolean = false;
        if (options?.isDesktopSiteMode) {
            if (sys.os === sys.OS.IOS) {
                if (nav.platform === 'MacIntel' && nav.maxTouchPoints && nav.maxTouchPoints > 1) {
                    isDesktopSiteMode = true;
                }
            }
        } else {
            isDesktopSiteMode = true;
        }

        const versionSupported = minVer !== undefined ? version >= minVer : true;
        const isSupported = versionSupported && isDesktopSiteMode;

        return {
            name,
            version,
            isSupported,
            details: `${name} v${version}`,
        };
    }
}
